import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, UserCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import CompanyLogo from "./layout/CompanyLogo";

const navBase = {
  display: "flex", alignItems: "center", gap: "8px",
  padding: "6px 12px", borderRadius: "8px",
  fontSize: "14px", fontWeight: 500, transition: "all 0.15s",
  textDecoration: "none",
};
const navActive = { backgroundColor: "#5e6ad2", color: "#ffffff" };
const navInactive = { color: "var(--lm-ink-2)", backgroundColor: "transparent" };

const PortalLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navLinkStyle = ({ isActive }) => ({ ...navBase, ...(isActive ? navActive : navInactive) });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Top navigation bar */}
      <header className="sticky top-0 z-30" style={{ backgroundColor: "rgba(1,1,2,0.88)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--lm-border)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Left: logo */}
          <div className="flex items-center gap-3 shrink-0">
            <CompanyLogo showCompanyName={true} />
          </div>

          {/* Center: nav links */}
          <nav className="hidden sm:flex items-center gap-1">
            <NavLink to="/portal/dashboard" style={navLinkStyle}
              onMouseEnter={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.color = "var(--lm-ink)"; }}
              onMouseLeave={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.color = "var(--lm-ink-2)"; }}
            >
              <LayoutDashboard className="w-4 h-4" />
              {t("portal.nav.dashboard", "Tableau de bord")}
            </NavLink>
            <NavLink to="/portal/mon-espace" style={navLinkStyle}
              onMouseEnter={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.color = "var(--lm-ink)"; }}
              onMouseLeave={(e) => { if (!e.currentTarget.getAttribute("aria-current")) e.currentTarget.style.color = "var(--lm-ink-2)"; }}
            >
              <UserCircle className="w-4 h-4" />
              {t("portal.nav.mySpace", "Mon Espace")}
            </NavLink>
          </nav>

          {/* Right: user info + language + logout */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm font-medium" style={{ color: "var(--lm-ink-2)" }}>
              {user?.first_name} {user?.last_name}
            </span>
            <LanguageSelector variant="minimal" />
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors"
              style={{ color: "#e5484d", border: "1px solid rgba(229,72,77,0.25)", backgroundColor: "transparent" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(229,72,77,0.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              title={t("sidebar.userMenu.logout")}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t("sidebar.userMenu.logout")}</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="sm:hidden flex items-center gap-1 px-4 pb-2">
          <NavLink to="/portal/dashboard" style={navLinkStyle}>
            <LayoutDashboard className="w-4 h-4" />
            {t("portal.nav.dashboard", "Tableau de bord")}
          </NavLink>
          <NavLink to="/portal/mon-espace" style={navLinkStyle}>
            <UserCircle className="w-4 h-4" />
            {t("portal.nav.mySpace", "Mon Espace")}
          </NavLink>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default PortalLayout;
